import { useState, useEffect } from 'react';
import { useAuthStore } from '../store/authStore';
import { useOfflineAuth } from '../hooks/useOfflineAuth';
import { useReferralCode, useReferralStats } from '../hooks/useReferrals';
import { Layout } from '../components/layout';
import { Card, Button, Loading, Modal } from '../components/common';
import { ProfileEditForm, PasswordChangeForm, PrivacySettings, DataExport, DataDeletion, TimezoneSettings } from '../components/profile';
import PerformanceDashboard from '../components/performance/PerformanceDashboard';
import { SecurityDashboard } from '../components/security/SecurityDashboard';
import { readingsAPI, type ReadingStats } from '../api/readings';
import { WifiOff, Copy, Check } from 'lucide-react';

export default function ProfilePage() {
  const { user } = useAuthStore();
  const { isOnline, canAccessReadingHistory } = useOfflineAuth();
  const { data: referralData, isLoading: isLoadingReferral } = useReferralCode();
  const { data: referralStats, isLoading: isLoadingReferralStats } = useReferralStats();
  const [stats, setStats] = useState<ReadingStats | null>(null);
  const [isLoadingStats, setIsLoadingStats] = useState(true);
  const [statsError, setStatsError] = useState<string | null>(null);
  const [showEditProfile, setShowEditProfile] = useState(false);
  const [showChangePassword, setShowChangePassword] = useState(false);
  const [showPerformance, setShowPerformance] = useState(false);
  const [showSecurity, setShowSecurity] = useState(false);
  const [showPrivacySettings, setShowPrivacySettings] = useState(false);
  const [showTimezoneSettings, setShowTimezoneSettings] = useState(false);
  const [showDataExport, setShowDataExport] = useState(false);
  const [showDataDeletion, setShowDataDeletion] = useState(false);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [copiedCode, setCopiedCode] = useState(false);
  const [copiedUrl, setCopiedUrl] = useState(false);

  // Helper function to ensure avatar URL is absolute
  const getAvatarUrl = (avatarUrl?: string) => {
    if (!avatarUrl) return null;
    // Ensure avatar URL is absolute
    if (avatarUrl.startsWith('http://') || avatarUrl.startsWith('https://')) {
      return avatarUrl;
    } else if (avatarUrl.startsWith('/')) {
      // Relative URL - prepend backend domain
      return `https://www.tarot-app.online${avatarUrl}`;
    }
    return avatarUrl;
  };

  useEffect(() => {
    // Only load stats if online
    if (isOnline) {
      loadStats();
    } else {
      setIsLoadingStats(false);
      setStatsError('Statistics unavailable offline');
      // Set default stats when offline
      setStats({
        totalReadings: 0,
        favoriteReadings: 0,
        recentReadings: [],
        readingsBySpread: [],
        readingsByStatus: []
      });
    }
  }, [isOnline]);

  const loadStats = async () => {
    try {
      setIsLoadingStats(true);
      setStatsError(null);
      const response = await readingsAPI.getStats();
      // Backend returns { data: { stats: {...} } }
      const statsData = (response.data as any).stats || response.data;
      
      // Transform backend response to match frontend ReadingStats interface
      const transformedStats: ReadingStats = {
        totalReadings: statsData.totalReadings || 0,
        favoriteReadings: statsData.favoriteReadings || 0,
        recentReadings: statsData.recent || [],  // Use 'recent' array, not 'recentReadings' number
        readingsBySpread: Object.entries(statsData.readingTypes || {}).map(([spreadId, count]) => ({
          spreadId,
          count: count as number
        })),
        readingsByStatus: Object.entries(statsData.statusBreakdown || {}).map(([status, count]) => ({
          status,
          count: count as number
        }))
      };
      
      setStats(transformedStats);
    } catch (error: any) {
      console.error('Failed to load stats:', error);
      setStatsError('Failed to load statistics');
      // Set default stats on error
      setStats({
        totalReadings: 0,
        favoriteReadings: 0,
        recentReadings: [],
        readingsBySpread: [],
        readingsByStatus: []
      });
    } finally {
      setIsLoadingStats(false);
    }
  };

  const getMembershipBenefits = (tier: string) => {
    switch (tier) {
      case 'pro':
        return [
          'Unlimited AI-powered readings',
          'Priority support',
          'Advanced spread layouts',
          'Export readings to PDF',
          'Custom tarot spreads',
          'Ad-free experience'
        ];
      case 'premium':
        return [
          '50 AI-powered readings per month',
          'Standard support',
          'All spread layouts',
          'Export readings',
          'Ad-free experience'
        ];
      case 'free':
      default:
        return [
          '5 AI-powered readings per month',
          'Basic spread layouts',
          'Reading history access',
          'Community support'
        ];
    }
  };

  const getMembershipColor = (tier: string) => {
    switch (tier) {
      case 'pro':
        return 'text-yellow-400';
      case 'premium':
        return 'text-purple-400';
      case 'free':
      default:
        return 'text-gray-400';
    }
  };

  const getFavoriteSpread = () => {
    if (!stats || stats.readingsBySpread.length === 0) {
      return 'None yet';
    }
    
    const sortedSpreads = [...stats.readingsBySpread].sort((a, b) => b.count - a.count);
    const favoriteSpreadId = sortedSpreads[0].spreadId;
    
    // Map spread IDs to names
    const spreadNames: Record<string, string> = {
      'single-card': 'Single Card',
      'three-card': 'Three Card',
      'celtic-cross': 'Celtic Cross'
    };
    
    return spreadNames[favoriteSpreadId] || favoriteSpreadId;
  };

  const handleEditSuccess = () => {
    setShowEditProfile(false);
    setSuccessMessage('Profile updated successfully!');
    setTimeout(() => setSuccessMessage(null), 5000);
  };

  const handlePasswordChangeSuccess = () => {
    setShowChangePassword(false);
    setSuccessMessage('Password changed successfully!');
    setTimeout(() => setSuccessMessage(null), 5000);
  };

  const handlePrivacySettingsSuccess = () => {
    setShowPrivacySettings(false);
    setSuccessMessage('Privacy settings updated successfully!');
    setTimeout(() => setSuccessMessage(null), 5000);
  };

  const handleTimezoneSettingsSuccess = () => {
    setShowTimezoneSettings(false);
    setSuccessMessage('Timezone settings updated successfully!');
    setTimeout(() => setSuccessMessage(null), 5000);
  };

  const handleCopyCode = async () => {
    if (referralData?.referralCode) {
      try {
        await navigator.clipboard.writeText(referralData.referralCode);
        setCopiedCode(true);
        setTimeout(() => setCopiedCode(false), 2000);
      } catch (err) {
        console.error('Failed to copy code:', err);
      }
    }
  };

  const handleCopyUrl = async () => {
    if (referralData?.shareUrl) {
      try {
        await navigator.clipboard.writeText(referralData.shareUrl);
        setCopiedUrl(true);
        setTimeout(() => setCopiedUrl(false), 2000);
      } catch (err) {
        console.error('Failed to copy URL:', err);
      }
    }
  };

  if (!user) {
    return (
      <Layout>
        <div className="container mx-auto px-4 py-8">
          <Card className="max-w-2xl mx-auto text-center">
            <p className="text-gray-400">Please log in to view your profile.</p>
          </Card>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="container mx-auto px-4 py-8">
        <div className="max-w-4xl mx-auto space-y-6">
          {/* Offline Notice */}
          {!isOnline && (
            <div className="p-4 bg-blue-900/20 border border-blue-500/30 rounded-lg">
              <div className="flex items-start gap-3">
                <WifiOff className="h-5 w-5 text-blue-400 mt-0.5" />
                <div className="flex-1">
                  <p className="text-blue-400 text-sm font-medium mb-1">
                    Viewing Profile Offline
                  </p>
                  <p className="text-blue-300 text-xs">
                    Some features like editing profile, changing password, and viewing statistics require an internet connection.
                  </p>
                </div>
              </div>
            </div>
          )}
          
          {/* Success Message */}
          {successMessage && (
            <div className="p-4 bg-green-900/20 border border-green-500/30 rounded-lg">
              <p className="text-green-400 text-sm">{successMessage}</p>
            </div>
          )}
          {/* Profile Header */}
          <Card variant="elevated">
            <div className="flex flex-col md:flex-row items-center md:items-start gap-6">
              {/* Avatar - 128x128 on profile page */}
              <div className="flex-shrink-0">
                {user.avatarUrl ? (
                  <img
                    src={getAvatarUrl(user.avatarUrl) || ''}
                    alt="Profile"
                    className="w-32 h-32 rounded-full object-cover"
                  />
                ) : (
                  <div className="w-32 h-32 rounded-full bg-gradient-to-br from-primary-500 to-secondary-500 flex items-center justify-center text-white text-3xl font-bold">
                    {(user.displayName || user.nickname || user.email).charAt(0).toUpperCase()}
                  </div>
                )}
              </div>
              
              {/* User Info */}
              <div className="flex-1 text-center md:text-left">
                <h1 className="text-3xl font-bold text-white mb-2">
                  {user.displayName || user.nickname || user.email}
                </h1>
                {user.nickname && (
                  <p className="text-gray-400 mb-1">@{user.nickname}</p>
                )}
                <p className="text-gray-400 mb-3">{user.email}</p>
                
                <div className="flex flex-wrap gap-2 justify-center md:justify-start">
                  <span className={`px-3 py-1 rounded-full text-sm font-semibold ${getMembershipColor(user.membershipTier)} bg-gray-800`}>
                    {user.membershipTier.toUpperCase()} Member
                  </span>
                  <span className="px-3 py-1 rounded-full text-sm text-gray-400 bg-gray-800">
                    Member since {new Date(user.registrationDate).toLocaleDateString('en-US', { month: 'short', year: 'numeric' })}
                  </span>
                </div>
              </div>
              
              {/* Action Buttons */}
              <div className="flex-shrink-0 flex gap-2">
                <Button 
                  variant="secondary" 
                  size="sm"
                  onClick={() => setShowEditProfile(true)}
                  disabled={!isOnline}
                  title={!isOnline ? 'Requires internet connection' : ''}
                >
                  Edit Profile
                  {!isOnline && <WifiOff className="h-3 w-3 ml-1" />}
                </Button>
                <Button 
                  variant="ghost" 
                  size="sm"
                  onClick={() => setShowChangePassword(true)}
                  disabled={!isOnline}
                  title={!isOnline ? 'Requires internet connection' : ''}
                >
                  Change Password
                  {!isOnline && <WifiOff className="h-3 w-3 ml-1" />}
                </Button>
                <Button 
                  variant="ghost" 
                  size="sm"
                  onClick={() => setShowPerformance(true)}
                >
                  Performance
                </Button>
                <Button 
                  variant="ghost" 
                  size="sm"
                  onClick={() => setShowSecurity(true)}
                >
                  Security
                </Button>
              </div>
            </div>
          </Card>

          {/* Statistics Dashboard */}
          <Card variant="elevated">
            <h2 className="text-2xl font-bold text-white mb-6">Your Statistics</h2>
            
            {isLoadingStats ? (
              <div className="flex justify-center py-8">
                <Loading size="lg" />
              </div>
            ) : statsError ? (
              <div className="text-center py-8">
                <p className="text-red-400 mb-4">{statsError}</p>
                {isOnline && (
                  <Button onClick={loadStats} variant="secondary" size="sm">
                    Retry
                  </Button>
                )}
                {!isOnline && (
                  <p className="text-gray-400 text-sm mt-2">
                    Connect to the internet to view your statistics
                  </p>
                )}
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <Card padding="sm" className="text-center">
                  <div className="text-4xl font-bold text-primary-500 mb-2">
                    {stats?.totalReadings || 0}
                  </div>
                  <p className="text-gray-400 text-sm">Total Readings</p>
                </Card>
                
                <Card padding="sm" className="text-center">
                  <div className="text-4xl font-bold text-secondary-500 mb-2">
                    {stats?.favoriteReadings || 0}
                  </div>
                  <p className="text-gray-400 text-sm">Favorite Readings</p>
                </Card>
                
                <Card padding="sm" className="text-center">
                  <div className="text-2xl font-bold text-purple-400 mb-2">
                    {getFavoriteSpread()}
                  </div>
                  <p className="text-gray-400 text-sm">Favorite Spread</p>
                </Card>
              </div>
            )}
          </Card>

          {/* Membership Benefits */}
          <Card variant="elevated">
            <h2 className="text-2xl font-bold text-white mb-4">
              Membership Benefits
            </h2>
            <p className="text-gray-400 mb-6">
              Your current plan: <span className={`font-semibold ${getMembershipColor(user.membershipTier)}`}>
                {user.membershipTier.toUpperCase()}
              </span>
            </p>
            
            <ul className="space-y-3">
              {getMembershipBenefits(user.membershipTier).map((benefit, index) => (
                <li key={index} className="flex items-start gap-3">
                  <span className="text-primary-500 mt-1">✓</span>
                  <span className="text-gray-300">{benefit}</span>
                </li>
              ))}
            </ul>
            
            {user.membershipTier === 'free' && (
              <div className="mt-6 pt-6 border-t border-gray-700">
                <p className="text-gray-400 mb-4">
                  Upgrade to unlock more features and unlimited readings!
                </p>
                <Button variant="primary" disabled>
                  Upgrade Membership
                </Button>
              </div>
            )}
          </Card>

          {/* Account Information */}
          <Card variant="elevated">
            <h2 className="text-2xl font-bold text-white mb-6">
              Account Information
            </h2>
            
            <div className="space-y-4">
              <div className="flex justify-between items-center py-3 border-b border-gray-700">
                <span className="text-gray-400">User ID</span>
                <span className="text-white font-mono text-sm">{user.userId}</span>
              </div>
              
              <div className="flex justify-between items-center py-3 border-b border-gray-700">
                <span className="text-gray-400">Email</span>
                <span className="text-white">{user.email}</span>
              </div>
              
              {user.nickname && (
                <div className="flex justify-between items-center py-3 border-b border-gray-700">
                  <span className="text-gray-400">Nickname</span>
                  <span className="text-white">@{user.nickname}</span>
                </div>
              )}
              
              {user.birthDate && (
                <div className="flex justify-between items-center py-3 border-b border-gray-700">
                  <span className="text-gray-400">Birth Date</span>
                  <span className="text-white">
                    {new Date(user.birthDate).toLocaleDateString('en-US', { 
                      year: 'numeric', 
                      month: 'long', 
                      day: 'numeric' 
                    })}
                    {user.zodiacSign && (
                      <span className="ml-2 text-primary-400">({user.zodiacSign})</span>
                    )}
                  </span>
                </div>
              )}
              
              <div className="flex justify-between items-center py-3 border-b border-gray-700">
                <span className="text-gray-400">Registration Date</span>
                <span className="text-white">
                  {new Date(user.registrationDate).toLocaleDateString('en-US', { 
                    year: 'numeric', 
                    month: 'long', 
                    day: 'numeric' 
                  })}
                </span>
              </div>
              
              <div className="flex justify-between items-center py-3">
                <span className="text-gray-400">Membership Tier</span>
                <span className={`font-semibold ${getMembershipColor(user.membershipTier)}`}>
                  {user.membershipTier.toUpperCase()}
                </span>
              </div>
            </div>
          </Card>

          {/* Referral Code Section */}
          <Card variant="elevated">
            <h2 className="text-2xl font-bold text-white mb-4">
              Refer a Friend
            </h2>
            <p className="text-gray-400 mb-6">
              Share your referral code and earn rewards when your friends join!
            </p>
            
            {!isOnline ? (
              <div className="p-4 bg-blue-900/20 border border-blue-500/30 rounded-lg">
                <div className="flex items-start gap-3">
                  <WifiOff className="h-5 w-5 text-blue-400 mt-0.5" />
                  <div className="flex-1">
                    <p className="text-blue-400 text-sm font-medium mb-1">
                      Offline Mode
                    </p>
                    <p className="text-blue-300 text-xs">
                      Connect to the internet to view your referral code
                    </p>
                  </div>
                </div>
              </div>
            ) : isLoadingReferral ? (
              <div className="flex justify-center py-8">
                <Loading size="md" />
              </div>
            ) : referralData ? (
              <div className="space-y-4">
                {/* Referral Code */}
                <div>
                  <label className="block text-sm font-medium text-gray-400 mb-2">
                    Your Referral Code
                  </label>
                  <div className="flex gap-2">
                    <div className="flex-1 bg-dark-800 border border-gray-600 rounded-lg px-4 py-3 font-mono text-2xl text-center text-primary-400 font-bold">
                      {referralData.referralCode}
                    </div>
                    <Button
                      variant="secondary"
                      size="lg"
                      onClick={handleCopyCode}
                      className="px-4"
                    >
                      {copiedCode ? <Check className="h-5 w-5" /> : <Copy className="h-5 w-5" />}
                    </Button>
                  </div>
                  {copiedCode && (
                    <p className="text-green-400 text-sm mt-2">✓ Code copied to clipboard!</p>
                  )}
                </div>

                {/* Share URL */}
                <div>
                  <label className="block text-sm font-medium text-gray-400 mb-2">
                    Share Link
                  </label>
                  <div className="flex gap-2">
                    <div className="flex-1 bg-dark-800 border border-gray-600 rounded-lg px-4 py-3 text-sm text-gray-300 truncate">
                      {referralData.shareUrl}
                    </div>
                    <Button
                      variant="secondary"
                      size="md"
                      onClick={handleCopyUrl}
                      className="px-4"
                    >
                      {copiedUrl ? <Check className="h-5 w-5" /> : <Copy className="h-5 w-5" />}
                    </Button>
                  </div>
                  {copiedUrl && (
                    <p className="text-green-400 text-sm mt-2">✓ Link copied to clipboard!</p>
                  )}
                </div>

                {/* Referral Stats */}
                {isLoadingReferralStats ? (
                  <div className="flex justify-center py-4">
                    <Loading size="sm" />
                  </div>
                ) : referralStats && (
                  <>
                    <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mt-6">
                      <Card padding="sm" className="text-center">
                        <div className="text-2xl font-bold text-primary-500 mb-1">
                          {referralStats.totalReferrals}
                        </div>
                        <p className="text-gray-400 text-xs">Total Friends</p>
                      </Card>
                      
                      <Card padding="sm" className="text-center">
                        <div className="text-2xl font-bold text-blue-500 mb-1">
                          {referralStats.qualifiedReferrals}
                        </div>
                        <p className="text-gray-400 text-xs">Qualified ($2+)</p>
                      </Card>
                      
                      <Card padding="sm" className="text-center">
                        <div className="text-2xl font-bold text-green-500 mb-1">
                          {referralStats.successfulReferrals}
                        </div>
                        <p className="text-gray-400 text-xs">Rewarded</p>
                      </Card>
                      
                      <Card padding="sm" className="text-center">
                        <div className="text-2xl font-bold text-yellow-500 mb-1">
                          {referralStats.pendingReferrals}
                        </div>
                        <p className="text-gray-400 text-xs">Pending</p>
                      </Card>
                      
                      <Card padding="sm" className="text-center">
                        <div className="text-2xl font-bold text-purple-400 mb-1">
                          {referralStats.totalRewardsEarned}
                        </div>
                        <p className="text-gray-400 text-xs">Coins Earned</p>
                      </Card>
                    </div>
                    
                    {/* Detailed Explanation */}
                    <div className="mt-4 p-4 bg-dark-800 border border-gray-700 rounded-lg">
                      <h4 className="text-sm font-semibold text-white mb-2">📊 Referral Status Breakdown</h4>
                      <div className="space-y-2 text-xs text-gray-300">
                        <div className="flex justify-between">
                          <span>• Total Friends (used your code):</span>
                          <span className="font-semibold text-primary-400">{referralStats.totalReferrals}</span>
                        </div>
                        <div className="flex justify-between">
                          <span>• Qualified Friends (spent $2+ total):</span>
                          <span className="font-semibold text-blue-400">{referralStats.qualifiedReferrals}</span>
                        </div>
                        <div className="flex justify-between">
                          <span>• Rewarded (you received coins):</span>
                          <span className="font-semibold text-green-400">{referralStats.successfulReferrals}</span>
                        </div>
                        <div className="flex justify-between">
                          <span>• Pending (not yet qualified):</span>
                          <span className="font-semibold text-yellow-400">{referralStats.pendingReferrals}</span>
                        </div>
                        <div className="flex justify-between pt-2 border-t border-gray-600">
                          <span className="font-semibold">Total Coins Earned:</span>
                          <span className="font-bold text-purple-400">{referralStats.totalRewardsEarned} 🪙</span>
                        </div>
                      </div>
                      {referralStats.qualifiedReferrals < referralStats.totalReferrals && (
                        <p className="mt-3 text-xs text-yellow-400">
                          💡 {referralStats.totalReferrals - referralStats.qualifiedReferrals} friend(s) need to spend $2+ to qualify for rewards
                        </p>
                      )}
                    </div>
                  </>
                )}

                <div className="mt-6 p-4 bg-primary-500/10 border border-primary-500/30 rounded-lg">
                  <p className="text-primary-400 text-sm">
                    💝 <strong>How it works:</strong> Share your code with friends. When they register and make their first purchase ($2+), you both receive bonus coins!
                  </p>
                </div>
              </div>
            ) : (
              <div className="text-center py-8">
                <p className="text-red-400 mb-4">Failed to load referral code</p>
                <Button onClick={() => window.location.reload()} variant="secondary" size="sm">
                  Retry
                </Button>
              </div>
            )}
          </Card>

          {/* Privacy & Data Management */}
          <Card variant="elevated">
            <h2 className="text-2xl font-bold text-white mb-6">
              Privacy & Data Management
            </h2>
            <p className="text-gray-400 mb-6">
              Control your privacy settings and manage your personal data.
            </p>
            
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
              <Card padding="sm" className="text-center">
                <div className="mb-3">
                  <div className="w-10 h-10 bg-primary-500/20 rounded-full flex items-center justify-center mx-auto mb-2 flex-shrink-0">
                    <svg className="w-5 h-5 flex-shrink-0" style={{width: '20px', height: '20px', minWidth: '20px', minHeight: '20px', maxWidth: '20px', maxHeight: '20px'}} fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                      <path className="text-primary-500" strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                    </svg>
                  </div>
                  <h3 className="text-base font-semibold text-white mb-1">Privacy Settings</h3>
                  <p className="text-gray-400 text-xs mb-3">
                    Control who can see your profile and readings
                  </p>
                </div>
                <Button 
                  variant="secondary" 
                  size="sm" 
                  onClick={() => setShowPrivacySettings(true)}
                  disabled={!isOnline}
                  className="w-full"
                  title={!isOnline ? 'Requires internet connection' : ''}
                >
                  Manage Privacy
                  {!isOnline && <WifiOff className="h-3 w-3 ml-1 inline" />}
                </Button>
              </Card>
              
              <Card padding="sm" className="text-center">
                <div className="mb-3">
                  <div className="w-10 h-10 bg-green-500/20 rounded-full flex items-center justify-center mx-auto mb-2 flex-shrink-0">
                    <svg className="w-5 h-5 flex-shrink-0" style={{width: '20px', height: '20px', minWidth: '20px', minHeight: '20px', maxWidth: '20px', maxHeight: '20px'}} fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                      <path className="text-green-500" strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                  </div>
                  <h3 className="text-base font-semibold text-white mb-1">Timezone Settings</h3>
                  <p className="text-gray-400 text-xs mb-3">
                    Set your preferred timezone for timestamps
                  </p>
                </div>
                <Button 
                  variant="secondary" 
                  size="sm" 
                  onClick={() => setShowTimezoneSettings(true)}
                  className="w-full"
                >
                  Manage Timezone
                </Button>
              </Card>
              
              <Card padding="sm" className="text-center">
                <div className="mb-3">
                  <div className="w-10 h-10 bg-blue-500/20 rounded-full flex items-center justify-center mx-auto mb-2 flex-shrink-0">
                    <svg className="w-5 h-5 flex-shrink-0" style={{width: '20px', height: '20px', minWidth: '20px', minHeight: '20px', maxWidth: '20px', maxHeight: '20px'}} fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                      <path className="text-blue-500" strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                    </svg>
                  </div>
                  <h3 className="text-base font-semibold text-white mb-1">Export Data</h3>
                  <p className="text-gray-400 text-xs mb-3">
                    Download a copy of your personal data
                  </p>
                </div>
                <Button 
                  variant="secondary" 
                  size="sm" 
                  onClick={() => setShowDataExport(true)}
                  disabled={!isOnline}
                  className="w-full"
                  title={!isOnline ? 'Requires internet connection' : ''}
                >
                  Export Data
                  {!isOnline && <WifiOff className="h-3 w-3 ml-1 inline" />}
                </Button>
              </Card>
              
              <Card padding="sm" className="text-center">
                <div className="mb-3">
                  <div className="w-10 h-10 bg-red-500/20 rounded-full flex items-center justify-center mx-auto mb-2 flex-shrink-0">
                    <svg className="w-5 h-5 flex-shrink-0" style={{width: '20px', height: '20px', minWidth: '20px', minHeight: '20px', maxWidth: '20px', maxHeight: '20px'}} fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                      <path className="text-red-500" strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                    </svg>
                  </div>
                  <h3 className="text-base font-semibold text-white mb-1">Delete Data</h3>
                  <p className="text-gray-400 text-xs mb-3">
                    Permanently remove your data
                  </p>
                </div>
                <Button 
                  variant="danger" 
                  size="sm" 
                  onClick={() => setShowDataDeletion(true)}
                  disabled={!isOnline}
                  className="w-full"
                  title={!isOnline ? 'Requires internet connection' : ''}
                >
                  Delete Data
                  {!isOnline && <WifiOff className="h-3 w-3 ml-1 inline" />}
                </Button>
              </Card>
            </div>
          </Card>
        </div>

        {/* Edit Profile Modal */}
        <Modal
          isOpen={showEditProfile}
          onClose={() => setShowEditProfile(false)}
          title="Edit Profile"
        >
          <ProfileEditForm
            onCancel={() => setShowEditProfile(false)}
            onSuccess={handleEditSuccess}
          />
        </Modal>

        {/* Change Password Modal */}
        <Modal
          isOpen={showChangePassword}
          onClose={() => setShowChangePassword(false)}
          title="Change Password"
        >
          <PasswordChangeForm
            onCancel={() => setShowChangePassword(false)}
            onSuccess={handlePasswordChangeSuccess}
          />
        </Modal>

        {/* Performance Dashboard Modal */}
        <Modal
          isOpen={showPerformance}
          onClose={() => setShowPerformance(false)}
          title="Performance Dashboard"
          size="xl"
        >
          <PerformanceDashboard />
        </Modal>

        {/* Security Dashboard Modal */}
        <Modal
          isOpen={showSecurity}
          onClose={() => setShowSecurity(false)}
          title="Security Dashboard"
          size="xl"
        >
          <SecurityDashboard />
        </Modal>

        {/* Privacy Settings Modal */}
        <Modal
          isOpen={showPrivacySettings}
          onClose={() => setShowPrivacySettings(false)}
          title="Privacy Settings"
          size="lg"
        >
          <PrivacySettings onSuccess={handlePrivacySettingsSuccess} />
        </Modal>

        {/* Timezone Settings Modal */}
        <TimezoneSettings
          isOpen={showTimezoneSettings}
          onClose={() => setShowTimezoneSettings(false)}
          onSuccess={handleTimezoneSettingsSuccess}
        />

        {/* Data Export Modal */}
        <Modal
          isOpen={showDataExport}
          onClose={() => setShowDataExport(false)}
          title="Export Your Data"
          size="lg"
        >
          <DataExport />
        </Modal>

        {/* Data Deletion Modal */}
        <Modal
          isOpen={showDataDeletion}
          onClose={() => setShowDataDeletion(false)}
          title="Delete Your Data"
          size="lg"
        >
          <DataDeletion />
        </Modal>
      </div>
    </Layout>
  );
}
